import React, { useState, useMemo } from 'react';
import { Card, Typography, Box, Stack, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import rise from '@iconify/icons-ant-design/rise';
import calendar from '@iconify/icons-ant-design/calendar';
import thunderbolt from '@iconify/icons-ant-design/thunderbolt';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
// import { useRevenueBreakdown } from 'src/hooks/useRevenueBreakdown.hook';

// Using a simple rupee symbol instead of icon
const RupeeIcon = () => (
  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#3182CE' }}>₹</span>
);

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.primary.light}20 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  textAlign: 'center',
}));

// Generate sample data for demonstration
const generateSampleData = (period) => {
  const now = new Date();
  const data = [];
  const labels = [];
  
  let days, startDate;
  
  switch (period) {
    case '7d':
      days = 7;
      startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      days = 30;
      startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      days = 90;
      startDate = new Date(now.getTime() - 89 * 24 * 60 * 60 * 1000);
      break;
    default:
      days = 7;
      startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
  }
  
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const baseRevenue = 1000 + Math.random() * 2000;
    const growthFactor = 1 + (i * 0.02); // 2% daily growth
    const revenue = Math.round(baseRevenue * growthFactor);
    
    data.push(revenue);
    labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  
  return { data, labels };
};

// Get chart options
const getChartOptions = (labels) => ({
  chart: {
    type: 'line',
    height: 350,
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    toolbar: {
      show: false,
    },
    background: 'transparent',
    animations: { enabled: false }
  },
  title: {
    text: 'Revenue Trends by Payment Method',
    align: 'left',
    style: {
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#1A202C'
    }
  },
  subtitle: {
    text: 'Daily revenue breakdown showing payment method distribution',
    align: 'left',
    style: {
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#718096'
    }
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  markers: {
    size: 4,
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  xaxis: {
    categories: labels,
    title: {
      text: 'Date',
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      }
    }
  },
  yaxis: {
    title: {
      text: 'Revenue (Rs)',
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      },
      formatter: (value) => `${value.toLocaleString()}`
    }
  },
  grid: {
    show: true,
    borderColor: '#E2E8F0',
    strokeDashArray: 3,
    xaxis: {
      lines: {
        show: false
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    fontSize: '12px',
    fontWeight: 500,
    markers: {
      radius: 5,
    },
    itemMargin: {
      horizontal: 8,
    },
    labels: {
      colors: '#4A5568'
    },
    offsetY: -10,
    floating: false,
    width: '100%',
    height: 'auto',
    formatter: function(seriesName, opts) {
      return seriesName;
    }
  },
  tooltip: {
    style: {
      fontSize: '12px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    y: {
      formatter: (value) => `${value.toLocaleString()}`
    }
  },
  dataLabels: {
    enabled: false
  }
});

// Process real revenue data from API
const processRevenueData = (apiData, period) => {
  if (!apiData || !apiData.data) {
    // Generate sample data for demonstration
    return generateSampleData(period);
  }

  const { data: revenueData, labels: apiLabels } = apiData;
  
  // If API provides labels, use them; otherwise generate based on period
  let labels = apiLabels;
  if (!labels || labels.length === 0) {
    const now = new Date();
    const data = [];
    const generatedLabels = [];
    
    let days, startDate;
    
    switch (period) {
      case '7d':
        days = 7;
        startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        days = 30;
        startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        days = 90;
        startDate = new Date(now.getTime() - 89 * 24 * 60 * 60 * 1000);
        break;
      default:
        days = 7;
        startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
    }
    
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      generatedLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    
    labels = generatedLabels;
  }

  // Ensure data is in the correct format
  const data = Array.isArray(revenueData) ? revenueData : [];
  
  return { data, labels };
};

// Process charging session data from transactions
const processChargingSessionData = (apiData, period) => {
  if (!apiData || !Array.isArray(apiData)) {
    return { data: [], labels: [] };
  }

  const { start, end } = period;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  // Initialize arrays for each day
  const totalSessions = new Array(days).fill(0);
  const successfulSessions = new Array(days).fill(0);
  const failedSessions = new Array(days).fill(0);
  const totalEnergyConsumed = new Array(days).fill(0);
  const labels = [];

  // Generate labels
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    labels.push(currentDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }));
  }

  // Process transactions
  apiData.forEach((txn) => {
    const txnDate = new Date(txn.start_time || txn.created_at);
    
    // Check if transaction is within our date range
    if (txnDate >= startDate && txnDate <= endDate) {
      const dayIndex = Math.floor((txnDate - startDate) / (24 * 60 * 60 * 1000));
      
      if (dayIndex >= 0 && dayIndex < days) {
        totalSessions[dayIndex]++;
        
        // Check if session was successful (has energy consumed and positive amount)
        const energyConsumed = parseFloat(txn.energy_consumed || 0);
        const amountCharged = parseFloat(txn.totalAmountCharged || 0);
        
        if (energyConsumed > 0 && amountCharged > 0) {
          successfulSessions[dayIndex]++;
          totalEnergyConsumed[dayIndex] += energyConsumed;
        } else {
          failedSessions[dayIndex]++;
        }
      }
    }
  });

  return {
    data: {
      totalSessions,
      successfulSessions,
      failedSessions,
      totalEnergyConsumed,
      labels
    }
  };
};

const RevenueTrendsChart = ({ 
  timeInterval, 
  transactionsData, 
  isLoading = false, 
  error = null
}) => {
  const [chartType, setChartType] = useState('revenue');


  // Determine period for chart display based on time interval
  const getPeriodFromTimeInterval = (timeInterval) => {
    if (!timeInterval || !timeInterval.type) return '7d';
    
    switch (timeInterval.type) {
      case 'Today':
        return '1d';
      case 'Last7Days':
        return '7d';
      case 'Last30Days':
        return '30d';
      case 'Last90Days':
        return '90d';
      case 'Custom':
        if (timeInterval.start && timeInterval.end) {
          const start = new Date(timeInterval.start);
          const end = new Date(timeInterval.end);
          const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          return diffDays <= 1 ? '1d' : diffDays <= 7 ? '7d' : diffDays <= 30 ? '30d' : '90d';
        }
        return '7d';
      default:
        return '7d';
    }
  };

  const period = getPeriodFromTimeInterval(timeInterval);

  // Process transactions data for revenue breakdown
  const revenueBreakdown = useMemo(() => {
    if (!transactionsData) {
      return null;
    }
    
    // Convert object to array if needed
    let transactionsArray;
    
    if (Array.isArray(transactionsData)) {
      transactionsArray = transactionsData;
    } else if (transactionsData && typeof transactionsData === 'object') {
      // Handle both regular objects and objects with numeric keys
      transactionsArray = Object.values(transactionsData);
    } else {
      return null;
    }
    
    if (!Array.isArray(transactionsArray) || transactionsArray.length === 0) {
      return null;
    }

    const { start, end } = timeInterval;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    
    // Initialize arrays for each day
    const totalRevenue = new Array(days).fill(0);
    const cardRevenue = new Array(days).fill(0);
    const walletRevenue = new Array(days).fill(0);
    const rfidRevenue = new Array(days).fill(0);
    const labels = [];

    // Generate labels
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      labels.push(currentDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }));
    }

    // Process transactions
    let processedCount = 0;
    let totalProcessedAmount = 0;
    
    transactionsArray.forEach((txn, index) => {
      const txnDate = new Date(txn.start_time || txn.created_at);
      
      
      // Check if transaction is within our date range
      if (txnDate >= startDate && txnDate <= endDate) {
        const dayIndex = Math.floor((txnDate - startDate) / (24 * 60 * 60 * 1000));
        
        if (dayIndex >= 0 && dayIndex < days) {
          // totalAmountCharged is already the net amount (refunds already deducted)
          const netAmount = parseFloat(txn.totalAmountCharged || 0);
          
          // Only add positive amounts to revenue
          if (netAmount > 0) {
            totalRevenue[dayIndex] += netAmount;
            processedCount++;
            totalProcessedAmount += netAmount;
            
            if (txn.payment_type === "CARD") {
              cardRevenue[dayIndex] += netAmount;
            } else if (txn.payment_type === "WALLET") {
              walletRevenue[dayIndex] += netAmount;
            } else if (txn.payment_type === "RFID") {
              rfidRevenue[dayIndex] += netAmount;
            }
          }
        }
      }
    });

    return {
      data: {
        totalRevenue,
        cardRevenue,
        walletRevenue,
        rfidRevenue,
        labels
      }
    };
  }, [transactionsData, timeInterval]);

  // Process charging session data
  const chargingSessionData = useMemo(() => {
    if (!transactionsData) {
      return null;
    }
    
    // Convert object to array if needed
    let transactionsArray;
    
    if (Array.isArray(transactionsData)) {
      transactionsArray = transactionsData;
    } else if (transactionsData && typeof transactionsData === 'object') {
      transactionsArray = Object.values(transactionsData);
    } else {
      return null;
    }
    
    if (!Array.isArray(transactionsArray) || transactionsArray.length === 0) {
      return null;
    }

    return processChargingSessionData(transactionsArray, timeInterval);
  }, [transactionsData, timeInterval]);

  const chartData = useMemo(() => {
    if (chartType === 'revenue') {
      if (!revenueBreakdown || !revenueBreakdown.data) {
        // Return empty data when no transactions
        const days = period === '1d' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 90;
        const emptyData = new Array(days).fill(0);
        const labels = [];
        
        for (let i = 0; i < days; i++) {
          const currentDate = new Date();
          currentDate.setDate(currentDate.getDate() - (days - 1 - i));
          labels.push(currentDate.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          }));
        }
        
        return {
          series: [
            {
              name: 'Total Revenue',
              data: emptyData,
              color: '#3182CE',
            },
            {
              name: 'Card Payments',
              data: emptyData,
              color: '#38A169',
            },
            {
              name: 'Wallet Payments',
              data: emptyData,
              color: '#ED8936',
            },
            {
              name: 'RFID Payments',
              data: emptyData,
              color: '#805AD5',
            },
          ],
          options: getChartOptions(labels, 'Revenue (Rs)')
        };
      }

      // Extract data from the processed transactions
      const { totalRevenue, cardRevenue, walletRevenue, rfidRevenue, labels } = revenueBreakdown.data;
      
      const datasets = [
        {
          name: 'Total Revenue',
          data: totalRevenue,
          color: '#3182CE',
        },
        {
          name: 'Card Payments',
          data: cardRevenue,
          color: '#38A169',
        },
        {
          name: 'Wallet Payments',
          data: walletRevenue,
          color: '#ED8936',
        },
        {
          name: 'RFID Payments',
          data: rfidRevenue,
          color: '#805AD5',
        },
      ];

      return {
        series: datasets,
        options: getChartOptions(labels, 'Revenue (Rs)')
      };
    } else {
      // Charging session chart
      if (!chargingSessionData || !chargingSessionData.data) {
        // Return empty data when no transactions
        const days = period === '1d' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 90;
        const emptyData = new Array(days).fill(0);
        const labels = [];
        
        for (let i = 0; i < days; i++) {
          const currentDate = new Date();
          currentDate.setDate(currentDate.getDate() - (days - 1 - i));
          labels.push(currentDate.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          }));
        }
        
        return {
          series: [
            {
              name: 'Total Sessions',
              data: emptyData,
              color: '#3182CE',
            },
            {
              name: 'Successful Sessions',
              data: emptyData,
              color: '#38A169',
            },
            {
              name: 'Failed Sessions',
              data: emptyData,
              color: '#E53E3E',
            },
          ],
          options: getChartOptions(labels, 'Charging Sessions')
        };
      }

      const { totalSessions, successfulSessions, failedSessions, labels } = chargingSessionData.data;
      
      const datasets = [
        {
          name: 'Total Sessions',
          data: totalSessions,
          color: '#3182CE',
        },
        {
          name: 'Successful Sessions',
          data: successfulSessions,
          color: '#38A169',
        },
        {
          name: 'Failed Sessions',
          data: failedSessions,
          color: '#E53E3E',
        },
      ];

      return {
        series: datasets,
        options: getChartOptions(labels, 'Charging Sessions')
      };
    }
  }, [period, revenueBreakdown, chargingSessionData, timeInterval, chartType]);

  // Handle empty data, loading state, or API endpoint not available
  if (isLoading || !chartData || !chartData.series || !chartData.series[0] || !chartData.series[0].data || chartData.series[0].data.length === 0) {
    const isApiUnavailable = !revenueBreakdown?.isRealData;
    
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {isLoading ? 'Loading...' : (isApiUnavailable ? 'Revenue API Not Available' : 'No Revenue Data Available')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 300 }}>
              {isLoading 
                ? 'Please wait while we load the revenue data...'
                : (isApiUnavailable 
                  ? 'The revenue trends API endpoint is not yet implemented. Please contact your administrator.'
                  : 'Revenue data will appear here once transactions are recorded.'
                )
              }
            </Typography>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  const totalRevenue = chartData.series[0].data.reduce((sum, val) => sum + val, 0);
  const currentRevenue = chartData.series[0].data[chartData.series[0].data.length - 1];
  const previousRevenue = chartData.series[0].data[chartData.series[0].data.length - 2];
  const growthRate = previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue * 100).toFixed(1) : 0;
  const avgDailyRevenue = Math.round(totalRevenue / chartData.series[0].data.length);

  // Calculate charging session metrics
  const totalSessions = chargingSessionData?.data?.totalSessions?.reduce((sum, val) => sum + val, 0) || 0;
  const totalSuccessfulSessions = chargingSessionData?.data?.successfulSessions?.reduce((sum, val) => sum + val, 0) || 0;
  const totalEnergyConsumed = chargingSessionData?.data?.totalEnergyConsumed?.reduce((sum, val) => sum + val, 0) || 0;
  const avgDailySessions = Math.round(totalSessions / (chargingSessionData?.data?.totalSessions?.length || 1));
  const successRate = totalSessions > 0 ? Math.round((totalSuccessfulSessions / totalSessions) * 100) : 0;

  // Loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading revenue data...
            </Typography>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  // Error state
  if (error) {
    return (
      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load revenue data. Please try again.
          </Alert>
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
              {chartType === 'revenue' ? 'Revenue Trends' : 'Charging Sessions'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {chartType === 'revenue' ? 'Track revenue performance over time' : 'Monitor charging session activity and success rates'}
            </Typography>
          </Box>
          
          {/* Chart Type Toggle */}
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(event, newChartType) => {
              if (newChartType !== null) {
                setChartType(newChartType);
              }
            }}
            size="small"
            sx={{ 
              '& .MuiToggleButton-root': {
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #E2E8F0',
                '&.Mui-selected': {
                  backgroundColor: '#3182CE',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#2C5AA0',
                  }
                }
              }
            }}
          >
            <ToggleButton value="revenue">
              <Icon icon={rise} width={16} height={16} style={{ marginRight: 8 }} />
              Revenue
            </ToggleButton>
            <ToggleButton value="sessions">
              <Icon icon={thunderbolt} width={16} height={16} style={{ marginRight: 8 }} />
              Sessions
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Key Metrics - 2 Rows */}
        <Box sx={{ mb: 3 }}>
          {chartType === 'revenue' ? (
            <>
              {/* First Row */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Box sx={{ marginRight: 1 }}>
                      <RupeeIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {totalRevenue.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Revenue ({timeInterval?.type || 'Custom'})
                  </Typography>
                </MetricCard>

                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <RupeeIcon />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {avgDailyRevenue.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Avg Daily
                  </Typography>
                </MetricCard>
              </Stack>
            </>
          ) : (
            <>
              {/* First Row */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#3182CE' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {totalSessions.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Sessions ({timeInterval?.type || 'Custom'})
                  </Typography>
                </MetricCard>

                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#38A169' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {avgDailySessions.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Avg Daily Sessions
                  </Typography>
                </MetricCard>
              </Stack>

              {/* Second Row */}
              {/* <Stack direction="row" spacing={2}>
                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#805AD5' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {successRate}%
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Success Rate
                  </Typography>
                </MetricCard>

                <MetricCard sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#D69E2E' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {totalEnergyConsumed.toFixed(1)} kWh
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Energy Consumed
                  </Typography>
                </MetricCard>
              </Stack> */}
            </>
          )}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, mt: 4 }}>
        <BaseChart
          key={`${chartType}-chart-${transactionsData?.length || 0}-${timeInterval?.type || 'default'}`}
          series={chartData.series}
          options={chartData.options}
          type="line"
          height={350}
        />
      </Box>
    </StyledCard>
  );
};

export default RevenueTrendsChart;
